/**
 * Module #34 Phase A chunk 6 — copilot memory management page.
 *
 * Route: /admin/copilot/memory. Lets staff see everything the Copilot
 * remembers (shared house memory + their own), forget (archive) / restore any
 * note, and permanently delete one (with an explicit confirm — the real wipe).
 *
 * Auth: inherits the /admin ProtectedRoute (tenant_owner / tenant_staff /
 * super_admin); the IE additionally gates the memory endpoints to staff.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button } from '@auctisync/ui';
import { useCopilotMemory, type CopilotMemoryItem } from '../../hooks/useCopilotMemory';

const alertStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
  fontSize: '0.9rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-muted)',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = { padding: '0.6rem 0.75rem', verticalAlign: 'top' };

function formatDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function AdminCopilotMemory() {
  const mem = useCopilotMemory();
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CopilotMemoryItem | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const active = mem.memories.filter((m) => !m.archived);
  const archived = mem.memories.filter((m) => m.archived);

  async function run(id: number, fn: () => Promise<boolean>, label: string): Promise<void> {
    setBusyId(id);
    setActionError(null);
    const ok = await fn();
    setBusyId(null);
    if (!ok) setActionError(`Couldn't ${label}. Please try again.`);
  }

  async function confirmDelete(): Promise<void> {
    if (pendingDelete === null) return;
    const id = pendingDelete.memoryId;
    setBusyId(id);
    setActionError(null);
    const ok = await mem.remove(id);
    setBusyId(null);
    if (ok) {
      setPendingDelete(null);
    } else {
      setActionError("Couldn't delete that memory. Please try again.");
    }
  }

  return (
    <div style={{ maxWidth: '64rem' }}>
      <header style={{ marginBottom: '1.25rem' }}>
        <Link to="/admin/copilot" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          ← Back to Copilot
        </Link>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: '0.4rem 0 0.3rem',
          }}
        >
          Copilot Memory
        </h1>
        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.95rem' }}>
          The Copilot saves durable facts and preferences here so it carries context across
          conversations — your house's setup and cadence, and how you like things done — instead of
          asking again every time. Forget anything you don't want it to keep (hidden, but
          recoverable); permanent delete wipes it for good.
        </p>
      </header>

      {mem.status.kind === 'loading' && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}

      {mem.status.kind === 'error' && (
        <div role="alert" style={alertStyle}>
          Couldn't load memory: {mem.status.detail}
        </div>
      )}

      {mem.status.kind === 'idle' && (
        <>
          {actionError !== null && (
            <div role="alert" style={alertStyle}>
              {actionError}
            </div>
          )}

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={sectionHeadingStyle}>What the Copilot remembers ({active.length})</h2>
            {active.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Nothing yet. The Copilot saves durable facts and preferences as it learns from your
                conversations — or just tell it “remember that we run sales on Saturdays.”
              </p>
            ) : (
              <MemoryTable
                items={active}
                busyId={busyId}
                actions={(m) => (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyId === m.memoryId}
                      onClick={() =>
                        void run(m.memoryId, () => mem.archive(m.memoryId), 'forget that')
                      }
                    >
                      Forget
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === m.memoryId}
                      onClick={() => setPendingDelete(m)}
                    >
                      Delete
                    </Button>
                  </>
                )}
              />
            )}
          </section>

          {archived.length > 0 && (
            <section>
              <h2 style={sectionHeadingStyle}>Forgotten ({archived.length})</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0 }}>
                The Copilot ignores these. Restore one to start using it again, or delete it
                permanently.
              </p>
              <MemoryTable
                items={archived}
                busyId={busyId}
                muted
                actions={(m) => (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busyId === m.memoryId}
                      onClick={() =>
                        void run(m.memoryId, () => mem.restore(m.memoryId), 'restore that')
                      }
                    >
                      Restore
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === m.memoryId}
                      onClick={() => setPendingDelete(m)}
                    >
                      Delete
                    </Button>
                  </>
                )}
              />
            </section>
          )}
        </>
      )}

      {pendingDelete !== null && (
        <ModalShell title="Permanently delete this memory?" onClose={() => setPendingDelete(null)}>
          <p style={{ color: 'var(--text-muted)', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
            This can’t be undone. The Copilot will permanently lose:
          </p>
          <p
            style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--surface-muted, #f8fafc)',
              borderRadius: 'var(--r-tile, 0.375rem)',
              fontSize: '0.9rem',
              color: 'var(--text)',
              marginBottom: '1rem',
            }}
          >
            “{pendingDelete.text}”
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyId === pendingDelete.memoryId}
              onClick={() => void confirmDelete()}
            >
              {busyId === pendingDelete.memoryId ? 'Deleting…' : 'Delete forever'}
            </Button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '0.6rem',
};

function MemoryTable({
  items,
  actions,
  busyId,
  muted,
}: {
  items: CopilotMemoryItem[];
  actions: (m: CopilotMemoryItem) => React.ReactNode;
  busyId: number | null;
  muted?: boolean;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        overflow: 'hidden',
        background: 'var(--surface)',
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface-elev)' }}>
              <th style={thStyle}>Scope</th>
              <th style={thStyle}>Memory</th>
              <th style={thStyle}>Last used</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr
                key={m.memoryId}
                style={{
                  borderTop: '1px solid var(--admin-hairline, var(--border))',
                  opacity: busyId === m.memoryId ? 0.5 : 1,
                }}
              >
                <td style={tdStyle}>
                  {m.scope === 'tenant' ? (
                    <Badge>Shared</Badge>
                  ) : (
                    <Badge variant="outline">Personal</Badge>
                  )}
                </td>
                <td style={{ ...tdStyle, color: muted ? 'var(--text-muted)' : 'var(--text)' }}>
                  {m.text.length > 0 ? (
                    <span style={{ fontWeight: 500 }}>{m.text}</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      (no description)
                    </span>
                  )}
                  <div
                    style={{ marginTop: '0.2rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}
                  >
                    {m.kind}
                    {m.subject !== null && m.subject.length > 0 ? ` · ${m.subject}` : ''}
                  </div>
                </td>
                <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {formatDate(m.lastUsedAt)}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'inline-flex', gap: '0.35rem' }}>{actions(m)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--admin-hairline, var(--border))',
          borderRadius: 'var(--r-card, 0.5rem)',
          padding: '1.25rem',
          width: '100%',
          maxWidth: '28rem',
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: '1.05rem',
            fontWeight: 600,
            color: 'var(--text)',
            marginBottom: '0.75rem',
          }}
        >
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
